/**
 * Hoe een concurrentprijs zich tot de onze verhoudt, en in welke volgorde de bronnen staan
 * (US-021, US-022).
 *
 * Twee dingen die op drie schermen hetzelfde moeten zijn — prijsoverzicht, prijshistorie en
 * productdetail — en die daarom hier staan en niet drie keer in een scherm.
 */
/** De grenzen in procenten. Onder 1% heet het gelijk: dat is ruis, geen positie in de markt. */
const GRENZEN = [
    { tot: 1, niveau: 'gelijk' },
    { tot: 3, niveau: 'licht' },
    { tot: 7, niveau: 'midden' },
];
/**
 * Het niveau en de richting van een verschil ten opzichte van onze eigen prijs.
 *
 * `goedkoper` betekent: de concurrent zit onder ons. Dat is de melding waar dit scherm voor bestaat,
 * dus die kant krijgt de aandacht-kleur. Let op dat dit een ándere betekenis is dan bij een
 * prijswijziging, waar rood "omhoog" betekent; daarom staat het verschil in een eigen vlakje en niet
 * in dezelfde vorm als de wijziging.
 */
export function beoordeelVerschil(procent) {
    if (procent === null)
        return null;
    const absoluut = Math.abs(procent);
    const niveau = GRENZEN.find((grens) => absoluut < grens.tot)?.niveau ?? 'sterk';
    return { niveau, goedkoper: procent < 0 };
}
/**
 * Bronnen op prijs, laag naar hoog, met de eigen bron altijd vooraan.
 *
 * De eigen bron blijft staan waar hij staat omdat alle percentages ertegen worden afgezet; hem
 * ergens in de rij laten opgaan maakt het ijkpunt zoekwerk. Dat kost niets aan leesbaarheid: staan de
 * concurrenten op prijs gesorteerd, dan is de eerste concurrent na ons de goedkoopste, en dus is in
 * één blik te zien of iemand onder ons zit.
 *
 * Bronnen zonder prijs gaan naar achteren. Ze horen niet tussen de prijzen te staan — je zou dan
 * moeten uitzoeken of daar nu een goedkope of een niet-gemeten bron staat — maar ze mogen ook niet
 * verdwijnen: een mislukte meting is iets om te zien.
 *
 * Stabiel: bij gelijke prijzen blijft de oorspronkelijke volgorde staan, zodat de kolommen niet van
 * plek wisselen bij een herlading.
 */
export function sorteerOpPrijs(bronnen, isEigen, prijs) {
    return bronnen
        .map((bron, index) => ({ bron, index }))
        .sort((a, b) => {
        const eigenA = isEigen(a.bron);
        const eigenB = isEigen(b.bron);
        if (eigenA !== eigenB)
            return eigenA ? -1 : 1;
        if (eigenA && eigenB)
            return a.index - b.index;
        const prijsA = prijs(a.bron);
        const prijsB = prijs(b.bron);
        if (prijsA === null && prijsB === null)
            return a.index - b.index;
        if (prijsA === null)
            return 1;
        if (prijsB === null)
            return -1;
        if (prijsA !== prijsB)
            return prijsA - prijsB;
        return a.index - b.index;
    })
        .map((rij) => rij.bron);
}
/**
 * De mediaan van een reeks getallen, of `null` bij een lege reeks.
 *
 * In het prijsoverzicht staat één kolommenrij boven álle producten, dus "de prijs van vandaag" is
 * daar niet één getal. De mediaan van de afwijking over de producten is dat wel, en die is
 * ongevoelig voor het ene product waar een bron uitzonderlijk duur of goedkoop is.
 */
export function mediaan(waarden) {
    if (waarden.length === 0)
        return null;
    const gesorteerd = [...waarden].sort((a, b) => a - b);
    const midden = Math.floor(gesorteerd.length / 2);
    return gesorteerd.length % 2 === 1
        ? gesorteerd[midden]
        : (gesorteerd[midden - 1] + gesorteerd[midden]) / 2;
}
//# sourceMappingURL=vergelijking.js.map